import { useState, useEffect, Fragment, useRef, memo } from "react";
import { Link, useNavigate, useParams } from "@reach/router";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import cx from "classnames";
import isEqual from "lodash/isEqual";
import { v4 } from "uuid";
import { ReactComponent as IconInfoCircle } from 'bootstrap-icons/icons/info-circle.svg'

import { formatDate } from "config/i18n";
import useQuery from "hooks/useQuery";
import getAccessToken from "hooks/getAccessToken";
import useMutationCustom from "hooks/useMutation";
import Country from "Components/Country/Country";
import Address from "Components/Address/Address";
import useUser from "hooks/useUser";
import Form from "../../Form/Form";
import Input from "../../Form/Input";
import EventForm from "../../Events/Form/Form";
import Emails from "../../Form/Emails/Emails";
import Text from "../../Form/Text";
import Phones from "../../Form/Phones/Phones";
import Financiers from "../../Form/Financiers/Financiers";
import Select from "../../Form/Select";
import useStoreRooms from "../../Room/useStore";
import verifyConsumer, { financierKey } from "../validateConsumer";
import getDate from "lib/getDate";
import SendFilesButton from "./SendFilesButton";
import ConsumerSharesHistory from "./ConsumerSharesHistory";

const { REACT_APP_API_URL } = process.env;
let clone = window.structuredClone || JSON.clone;

// valores validos de genero; si viene otro (ej. "select") se normaliza a vacio
const GENDER_VALID_VALUES = ["female", "male", "other"];

const INTIAL_EVENTS_LIMIT = 20;

// Normaliza un valor para comparar "sucio" sin falsos positivos: null/undefined
// se tratan como "" y las claves vacías se descartan, de modo que null == "" ==
// campo ausente y {} == {email:""} == fila placeholder. Necesario porque el form
// reshapea current respecto de original (ej. financiers ||= [] en verifyConsumer,
// seed [{}] vs [] del server) y isEqual es estricto.
const normalizeForDirtyCheck = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) {
    const items = value
      .map(normalizeForDirtyCheck)
      .filter(
        (item) =>
          !(
            item &&
            typeof item === "object" &&
            !Array.isArray(item) &&
            Object.keys(item).length === 0
          )
      );
    return items.length === 0 ? "" : items;
  }
  if (typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        const normalized = normalizeForDirtyCheck(value[key]);
        if (normalized !== "") acc[key] = normalized;
        return acc;
      }, {});
  }
  return value;
};

const fetchMutate = (token) => (payload) => {
  const url = `${REACT_APP_API_URL}${window._l(`/consumers/merge`)}`;

  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  }).then((res) => res.json());
};

const Event = memo(({ event, consumer, onEventChange }) => {
  const { t } = useTranslation();
  const openRoom = useStoreRooms((state) => state.open);
  const [form, setForm] = useState(false);

  return (
    <>
      {/* Bug real (auditoría 2026-08-22): la fecha del turno (puede ser
          larga, ej. "Martes 15 Julio 2026 14:00") + íconos a la izquierda,
          vs. servicio + profesional a la derecha, sin flex-wrap — en mobile
          competían por la misma línea en vez de apilarse. */}
      <div className="bg-white shadow px-4 py-3 rounded mt-4 d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <div className="d-flex align-items-center">
            <button
              className="btn btn-link p-0 text-first-up"
              onClick={() => setForm(!form)}
            >
              {formatDate(event.startsAt, "EEEE d MMMM yyyy HH:mm")}
            </button>
            {event.remote && (
              <button
                data-tooltip-id="app-tooltip"
                data-tooltip-content={t("start_remote_service", "Iniciar videoconsulta")}
                className="btn btn-link ms-1 p-0"
                onClick={() => {
                  openRoom(`${event.id}/${event.token}`);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-camera-video-fill text-dark ms-2"
                  viewBox="0 0 16 16"
                >
                  <path
                    fillRule="evenodd"
                    d="M0 5a2 2 0 0 1 2-2h7.5a2 2 0 0 1 1.983 1.738l3.11-1.382A1 1 0 0 1 16 4.269v7.462a1 1 0 0 1-1.406.913l-3.111-1.382A2 2 0 0 1 9.5 13H2a2 2 0 0 1-2-2V5z"
                  />
                </svg>
              </button>
            )}
            {event.source !== "drmanager" && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="bi ms-2"
                data-tooltip-id="app-tooltip"
                data-tooltip-content={t(`source_${event.source}`)}
                height="16"
                width="16"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
          <div>
            <div
              className={cx({
                "badge bg-warning": event.status === "arrived",
                "badge bg-primary": event.status === "active",
                "badge bg-success": event.status === "fulfilled",
                "badge bg-danger": ["cancelled", "noshow"].includes(
                  event.status
                ),
                "badge bg-dark": event.status === "booked",
              })}
            >
              {t(`status_${event.status}`)}
            </div>
          </div>
        </div>
        <div className="text-end">
          <div>{t(event.service.key, event.service.label)}</div>
          <div className="text-secondary">{event.resource.label}</div>
        </div>
      </div>
      {form && (
        // Bug real (auditoría 2026-08-25, reportado por Juan Cruz QA): 390px
        // (menos el p-4) le deja al formulario ~342px de contenido — la fila
        // de fecha/hora de Events/Form/Form.js necesita ~367px sin envolver
        // (medido: 221px fecha + 138px hora + 8px gap), así que pasaba a dos
        // líneas incluso en desktop. Se agranda el ancho base para dar
        // margen real, mismo criterio que el offcanvas de Navbar.js y el
        // popover de bloqueo en Calendar.js.
        <div
          className="p-4 mt-3 shadow bg-white rounded"
          style={{ width: "100%", maxWidth: "min(470px, calc(100vw - 48px))" }}
        >
          <EventForm
            eventID={event.id}
            onClose={() => setForm(false)}
            onChange={(newEvent) => setForm(false)}
            consumer={consumer}
          />
        </div>
      )}
    </>
  );
});

const Candidate = (props) => {
  const { t, i18n } = useTranslation();
  const accessToken = getAccessToken();
  const [merged, setMerged] = useState(false);
  const { mutate } = useMutation(fetchMutate(accessToken.value), {
    onMutate: (payload) => {
      setMerged(true);
    },
    onSuccess: (data) => {
      if (data.id !== props.consumerID) {
        props.onSelect(data.id);
      }
    },
  });

  const merge = () => {
    if(!window.confirm(t("merge_confirm", "¿Está seguro que desea fusionar los pacientes? Esta acción no se puede deshacer."))) return;
    mutate([props.consumerID, props.candidate.id]);
  };

  const select = () => {
    props.onSelect(props.candidate.id);
  };

  return (
    // Bug real (auditoría 2026-08-22): el nombre del paciente candidato
    // (puede ser largo, apellidos compuestos) vs. los botones Seleccionar/
    // Fusionar, sin flex-wrap — en mobile podían empujarse fuera de la fila.
    <div className="d-flex align-items-center justify-content-between bg-light-blue px-3 py-2 flex-wrap gap-2">
      <div>
        <div>
          <a
            href={window._l(`/${props.candidate.id}`)}
            target={encodeURIComponent(props.candidate.id)}
          >
            {props.candidate.label}
          </a>
        </div>
        <small className="text-secondary">
          {parseInt(
            props.candidate.identification?.replace(/\D/g, "")
          ).toLocaleString("es")}
        </small>
      </div>
      {!merged ? (
        <div>
          <button
            type="button"
            className="btn btn-sm bg-warning text-dark"
            data-tooltip-id="app-tooltip"
            data-tooltip-content={t("merge_select_tip", "Carga los datos de este paciente")}
            onClick={select}
          >
            {t("select")}
          </button>
          {props.consumerID && (
            <button
              type="button"
              data-tooltip-id="app-tooltip"
              data-tooltip-content={t(
                "merge_consumer_tip",
                "Al fusionar los pacientes quedan unificados todos los datos del paciente en uno solo"
              )}
              className="btn btn-sm bg-warning text-dark ms-2"
              onClick={merge}
            >
              {t("merge_consumer", "Fusionar")}
            </button>
          )}
        </div>
      ) : (
        <div className="text-sm text-success">{t("merged", "fusionado")}</div>
      )}
    </div>
  );
};

const App = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const user = useUser();
  const [consumerID, setConsumerID] = useState(null);
  const [warnings, setWarnings] = useState(props.warnings || {});
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(() => () => { });
  const [confirmMessage, setConfirmMessage] = useState("");
  const [actionLabel, setActionLabel] = useState();
  const [showModal, setShowModal] = useState(false);
  const [visiblePastCount, setVisiblePastCount] = useState(INTIAL_EVENTS_LIMIT);
  const pastEndRef = useRef(null);

  // Bug real (UX, 2026-08-04): el link de "Facturar sesiones" se mostraba
  // para CUALQUIER profesional con resource propio, sin importar si el team
  // tiene el feature flag ARCA prendido (opt-in por team, ver Setup/Resources/
  // Form/Form.js#canArcaBilling y Events/Agenda/Event/Facturar.js, los otros
  // 2 puntos de entrada de la app — ambos SÍ chequean esto antes de mostrarse)
  // — para cualquier team SIN el flag (la mayoría, todavía no es todos), el
  // profesional veía el link en CADA ficha de paciente y, al tocarlo,
  // aterrizaba en una página en blanco (Facturar.js se auto-oculta del todo
  // si !arcaEnabled) sin ningún mensaje ni forma de volver salvo el botón
  // atrás del navegador.
  const arcaEnabledQuery = useQuery(window._l('/arca/enabled').substr(1), { enabled: !!user.team?.resourceID, staleTime: 30000 })
  const arcaEnabled = !!arcaEnabledQuery.data?.enabled

  const today = getDate();

  const openConfirm = (action, message, actionLabel) => {
    setConfirmAction(() => action);
    setActionLabel(actionLabel);
    setConfirmMessage(message);
    setShowConfirm(true);
  };

  // Bug real (auditoría 2026-08-14): esta función RECORTA warnings al solo
  // financiers/flag — los 3 call sites de abajo tenían la condición
  // invertida (`!== "prescription"`), así que ese recorte se aplicaba en
  // el flujo NORMAL (home/modal, el más usado) y se salteaba en
  // "prescription" (el menos usado) — exactamente al revés de lo que tiene
  // sentido: el mini-form de prescripción no muestra DNI/género/fecha de
  // nacimiento, esos warnings ahí son ruido; el form completo de
  // consumer SÍ los muestra y necesita conservarlos.
  const getFinancierWarnings = (object) => {
    return clone({ financiers: object?.financiers, flag: object?.financiers?.flag })
  }

  // Hallazgo real de review: un envío exitoso hecho DESDE la ficha no
  // refrescaba "Archivos enviados" (fetch único por consumerID, sin forma
  // de que el modal le avise al padre) — bump-ear esto fuerza el refetch.
  const [sharesRefreshKey, setSharesRefreshKey] = useState(0);

  // Checkbox "paciente prematuro" que gatea el input de semanas de
  // gestación (ver isPediatric más arriba). Arranca en false y se
  // inicializa en true recién cuando cargan datos reales de un paciente
  // que YA tenía semanas de gestación cargadas (ver el useEffect de
  // query.data más abajo) — así una edición existente no esconde un dato
  // que el profesional ya había cargado antes de este fix.
  const [isPremature, setIsPremature] = useState(false);

  const [current, setCurrent] = useState({
    firstName: "",
    lastName: "",
    occupation: "",
    address: "",
    dob: "",
    // Card "Mejoras de UX en Percentiles" (22/09), punto 3: semanas de
    // gestación al nacer — dato de nacimiento, mismo criterio que `dob`
    // (vive en el consumer, no en percentiles ni en un record de
    // vitalSigns). Gateado en el JSX (isPediatric + checkbox "prematuro",
    // ver más abajo) — bug real reportado por Juan Cruz 23/09: mostrarlo
    // siempre, para cualquier paciente, generaba consultas/reclamos.
    gestationalAgeWeeks: "",
    country: "",
    identification: "",
    blocked: false,
    gender: "",
    phones: [{}],
    emails: [{}],
    financiers: [{}],
    ...props.initialState,
  });

  // Bug real (Juan Cruz, card Percentiles 23/09): "semanas de gestación"
  // aparecía SIEMPRE, para cualquier paciente — un dato que solo aplica a
  // prematuros pediátricos generaba consultas/reclamos de clientes al verlo
  // en la ficha de un adulto. Mismo corte de edad y mismo criterio de
  // parseo estricto ("YYYY-MM-DD", nada de new Date(dobRaw) — ver
  // EHR.js#isPediatric) que ya usa la HCE para decidir si mostrar
  // percentiles — una sola definición de "pediátrico", no dos que puedan
  // desalinearse con el tiempo.
  const dobIsoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(current.dob || "");
  const dobMsForAge = dobIsoMatch
    ? Date.UTC(Number(dobIsoMatch[1]), Number(dobIsoMatch[2]) - 1, Number(dobIsoMatch[3]))
    : null;
  const consumerAgeYears = Number.isFinite(dobMsForAge)
    ? (Date.now() - dobMsForAge) / (365.25 * 24 * 60 * 60 * 1000)
    : null;
  const isPediatric = consumerAgeYears != null && consumerAgeYears >= 0 && consumerAgeYears < 19;

  const formPropagated = useRef(false);

  const [debouncedIdentification, setDebouncedIdentification] = useState(current.identification);
  const identificationTimerRef = useRef(null);

  useEffect(() => {
    clearTimeout(identificationTimerRef.current);
    identificationTimerRef.current = setTimeout(
      () => setDebouncedIdentification(current.identification),
      250
    );
    return () => clearTimeout(identificationTimerRef.current);
  }, [current.identification]);

  const candidates = useQuery(window._l(`/search/consumers`).substr(1), {
    initialData: [],
    enabled: debouncedIdentification?.length >= 6,
    body: { q: debouncedIdentification, sort: { key: "label" } },
  });

  const _candidates = candidates.data.filter(
    (candidate) => consumerID !== candidate.id
  );

  const [original, setOriginal] = useState(clone(current));

  const events = useQuery(window._l(`/${consumerID}/events`).substr(1), {
    initialData: { past: [], next: [] },
    enabled: !!consumerID && props.events !== false,
  });

  const pastEvents = events.data?.past ?? [];
  const hasMorePast = pastEvents.length > visiblePastCount;

  useEffect(() => {
    setVisiblePastCount(INTIAL_EVENTS_LIMIT);
  }, [consumerID]);

  useEffect(() => {
    if (!hasMorePast || !pastEndRef.current) return;
    const el = pastEndRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        setVisiblePastCount((prev) => Math.min(prev + INTIAL_EVENTS_LIMIT, pastEvents.length));
      },
      { rootMargin: "100px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMorePast, pastEvents.length]);

  const query = useQuery(window._l(`/${consumerID}`).substr(1), {
    enabled: !!consumerID,
  });



  const [mutate, mutation] = useMutationCustom(
    window._l(`/${consumerID || "consumers"}`).substr(1),
    {
      onMutate: (payload) => {
        payload.label = [payload.lastName, payload.firstName]
          .filter(Boolean)
          .join(", ");
        setOriginal(clone(payload));
        setCurrent(clone(payload));
      },
      onSuccess: (data) => {
        if (!consumerID && data.id) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'paciente_creado',
            team_id: data.teamID,
            source: 'manual',
          });
          setShowModal(true);
        }

        if (props.onSave) {
          props.onSave(clone(data));
        } else if (props.callback) {
          props.callback(clone(data));
        } else if (!consumerID && data.id) {
          navigate(`../${data.id}`);
        }
      },
      onError: (error) => {
        window.notify({
          message: error.message,
          type: "danger",
          title: "Error",
        });
      },
    }
  );

  const disabled = consumerID && !user.actions.can("write", current);
  const loading = query.isLoading || mutation.isLoading;
  const changed =
    !isEqual(original, current) || !current.id || /^new/.test(current.id);
  isEqual(original, current);

  // El modal contenedor pregunta antes de cerrar si hay datos sin guardar.
  // Comparamos original vs current normalizados (no `changed`, que en alta es
  // siempre true por falta de id) para evitar falsos positivos por diferencias
  // de forma (null vs "" vs {} vs filas placeholder) que no son cambios reales.
  useEffect(() => {
    props.onDirtyChange?.(
      !isEqual(normalizeForDirtyCheck(original), normalizeForDirtyCheck(current))
    );
  }, [current, original]);

  const toggle = () => {
    if (disabled) return;
    current.deleted = !current.deleted;
    setCurrent({ ...current });
    mutate(current);
  };

  const onChange = ({ target: { name, value, type } }) => {
    if (name === "dob" && value && value > today) {
      return;
    }
    if (value && type === "number") {
      current[name] = +value;
    } else if (type === "checkbox") {
      current[name] = !current[name];
    } else {
      current[name] = value;
    }

    let warnings = verifyConsumer(current);
    if(props.source === "prescription"){
      warnings = getFinancierWarnings(warnings);
    }
    setWarnings(warnings)
    setCurrent(clone(current));
  };

  const onSelectCandidate = (candidateID) => {
    if (params?.consumerID) {
      navigate(`../${candidateID}`);
    } else {
      setConsumerID(candidateID);
    }
  };

  const onChangeFinancier = (index) => ({ target: { name, value, type } }) => {
    if (value && type === "number") {
      current.financiers[index][name] = +value;
    } else if (type === "checkbox") {
      current.financiers[index][name] = !current.financiers[index][name];
    } else {
      current.financiers[index][name] = value;
    }
    onChange({
      target: { name: "financiers", value: [...current.financiers] },
    });
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (disabled) return;
    let warnings = verifyConsumer(current);
    if (props.source === "prescription") {
      warnings = getFinancierWarnings(warnings);
    }
    setWarnings(warnings);
    mutate(current, {
      onSuccess: (data) => {
        if (!consumerID && data.id) {
          setShowModal(true);
        }
        if (props.onSave) {
          props.onSave(clone(data));
        } else if (props.callback) {
          props.callback(clone(data));
        } else if (!consumerID && data.id) {
          navigate(`../${data.id}`);
        }
      },
      onError: (error) => {
        window.notify({
          message: error.message,
          type: "danger",
          title: "Error",
        });
      },
    });
  };

  useEffect(() => {
    if (query.data?.id) {
      const original = clone(query.data);
      if (original.emails.length === 0) {
        original.emails.push({});
      }
      if (original.phones.length === 0) {
        original.phones.push({});
      }
      if (original.financiers.length === 0) {
        original.financiers.push({});
      }
      // normaliza genero invalido (ej. "select") para que el select muestre el placeholder
      if (original.gender && !GENDER_VALID_VALUES.includes(original.gender)) {
        original.gender = "";
      }
      const current = clone(original);
      setCurrent(current);
      setOriginal(original);
      setIsPremature(!!original.gestationalAgeWeeks);
    }
  }, [query.data]);

  const isBlocked = !!current?.blocked;

  const handleBlockClick = () => {
    let updatedBlockedBy = !current?.blocked;

    setCurrent({
      ...current,
      blocked: updatedBlockedBy,
    });
  };

  useEffect(() => {
    if (query.data?.id && !props.warnings) {
      let warnings = verifyConsumer(query.data);
      if(props.source === "prescription"){
        warnings = getFinancierWarnings(warnings);
      }
      setWarnings(warnings);
    }
  }, [query.data]);

  useEffect(() => {
    if (props.consumerID === "new" || !props.consumerID) {
      setConsumerID(null);
    } else if (/^consumers\//.test(props.consumerID)) {
      setConsumerID(props.consumerID);
    } else {
      setConsumerID(`consumers/${props.consumerID}`);
    }
  }, [props.consumerID]);

  if (disabled) {
    return (
      <div
        className={[
          "d-flex",
          "flex-column",
          "justify-content-center",
          "align-items-center",
          props.className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div style={{ width: "min(480px, calc(100vw - 32px))" }}>
          <div className="card bg-warning">
            <div className="card-header">{t("request_access_title")}</div>
            <div className="card-body">
              <h5 className="card-title">{current.label}</h5>
              <p className="card-text">{t("request_access_message")}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Form
        onSubmit={onSubmit}
        skip={!changed}
        className={[props.className].filter((p) => p).join(" ") + ` ${showConfirm ? "position-relative blur" : ""}`}
      >
        <div>
          <Input
            autoFocus={!current.firstName}
            warning={!!warnings.firstName}
            label={t("firstName", { context: "consumers" })}
            disabled={disabled}
            autoCapitalize="words"
            required
            minLength="2"
            maxLength="32"
            pattern="[A-Za-zÀ-ÖØ-öø-ÿ .']{2,32}"
            type="text"
            name="firstName"
            id="firstName"
            placeholder={t("firstName_placeholder", {
              context: "consumers",
            })}
            value={current.firstName || ""}
            onChange={onChange}
            hint={t("firstName_hint", { context: "consumers" })}
          />


          <Input
            warning={!!warnings.lastName}
            label={t("lastName", { context: "consumers" })}
            disabled={disabled}
            autoCapitalize="words"
            required
            minLength="2"
            maxLength="32"
            pattern="[A-Za-zÀ-ÖØ-öø-ÿ .']{2,32}"
            type="text"
            name="lastName"
            id="lastName"
            placeholder={t("lastName_placeholder", {
              context: "consumers",
            })}
            value={current.lastName || ""}
            onChange={onChange}
            hint={t("lastName_hint", { context: "consumers" })}
          />

          <div className="d-flex flex-wrap gap-2 mb-3">
            <div style={{ minWidth: '100px' }}>
              <Select
                label={t("identification_type", "Tipo")}
                disabled={disabled}
                id="identificationType"
                name="identificationType"
                value={current.identificationType || "dni"}
                onChange={onChange}
                placeholder={t("identification_type_placeholder", "Tipo")}
                options={[
                  { value: "dni", label: t("dni", "DNI") },
                  { value: "pasaporte", label: t("pasaporte", "Pasaporte") },
                  { value: "ced", label: t("ced", "Cédula") },
                  { value: "le", label: t("le", "LE") }
                ]}
                className="mb-0"
              />
            </div>
            <div className="flex-grow-1">
              <Input
                warning={!!warnings.identification}
                label={t("identification_number", { context: "consumers", defaultValue: "Identificación" })}
                disabled={disabled}
                minLength="4"
                maxLength="32"
                type="text"
                name="identification"
                id="identification"
                placeholder={t("identification_placeholder", {
                  context: "consumers",
                })}
                value={current.identification || ""}
                onChange={onChange}
                hint={t("identification_hint", { context: "consumers" })}
              />
            </div>
          </div>
          {current.identification?.length >= 6 &&
            !mutation.isLoading &&
            _candidates.length > 0 && (
              <div className="border rounded shadow-sm mb-3 overflow-hidden">
                <h6 className="px-3 pt-3">
                  {t(
                    "consumer_canidates_same_identification",
                    "Pacientes con el mismo DNI"
                  )}
                </h6>
                {_candidates.map((candidate) => (
                  <Candidate
                    key={candidate.id}
                    candidate={candidate}
                    consumerID={consumerID}
                    onSelect={onSelectCandidate}
                  />
                ))}
              </div>
            )}

          <Select
            warning={!!warnings.gender}
            label={t("gender")}
            disabled={disabled || /dr|dra/g.test(current.namePrefix)}
            id="gender"
            name="gender"
            value={GENDER_VALID_VALUES.includes(current.gender) ? current.gender : ""}
            onChange={onChange}
            placeholder={t("gender_placeholder")}
            options={GENDER_VALID_VALUES.map((value) => ({
              value,
              label: t(value),
            }))}
            hint={t("gender_hint", { context: "consumers" })}
          />

          <Input
            warning={!!warnings.dob}
            disabled={disabled}
            minLength="10"
            maxLength="10"
            type="date"
            pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}"
            max={today}
            name="dob"
            id="dob"
            placeholder={t("dob_placeholder")}
            value={current.dob || ""}
            onChange={onChange}
            label={t("dob")}
            hint={t("dob_hint")}
            formats={["no-inputtype-date"]}
          />

          {/* Bug real (Juan Cruz, card Percentiles 23/09): antes esto se
              mostraba para CUALQUIER paciente — un dato irrelevante para
              adultos que generaba consultas/reclamos. Ahora: checkbox solo
              para pediátricos, e input solo si además está tildado
              "prematuro". No pediátrico -> ninguno de los dos aparece. */}
          {isPediatric && (
            <div className="form-check mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="isPremature"
                disabled={disabled}
                checked={isPremature}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsPremature(checked);
                  // Al destildar, "no es prematuro" — no dejar semanas de
                  // gestación cargadas de forma invisible en el payload.
                  if (!checked) {
                    setCurrent({ ...current, gestationalAgeWeeks: "" });
                  }
                }}
              />
              <label className="form-check-label" htmlFor="isPremature">
                {t("is_premature", { context: "consumers", defaultValue: "Paciente prematuro" })}
              </label>
            </div>
          )}

          {isPediatric && isPremature && (
            <Input
              disabled={disabled}
              type="number"
              min="22"
              max="42"
              step="1"
              name="gestationalAgeWeeks"
              id="gestationalAgeWeeks"
              placeholder={t("gestational_age_weeks_placeholder", { context: "consumers", defaultValue: "Semanas" })}
              value={current.gestationalAgeWeeks || ""}
              onChange={onChange}
              label={t("gestational_age_weeks", { context: "consumers", defaultValue: "Semanas de gestación al nacer" })}
              hint={t("gestational_age_weeks_hint", { context: "consumers", defaultValue: "Solo si nació antes de la semana 37 — se usa para corregir la edad en percentiles." })}
            />
          )}

          <div className="mb-3">
            <label className="form-label" htmlFor="country">
              {t("country", { context: "consumers" })}
            </label>
            <Country
              name="country"
              id="country"
              required
              value={current.country || ""}
              onChange={(country) =>
                onChange({
                  target: {
                    name: "country",
                    value: country,
                  },
                })
              }
            />
            <div
              className="form-text"
              dangerouslySetInnerHTML={{
                __html: t(`country_hint`, { context: "consumers" }),
              }}
            />
          </div>

          <div className="mb-3">
            <div className="d-flex align-items-center form-label">
              <label htmlFor="financierValue" className={!!warnings.financiers?.flag ? 'fw-bold text-danger' : ''}>
                {t("financier_plural", { context: "consumers" })}
              </label>
              {!disabled && (
                <button
                  onClick={() =>
                    setCurrent({
                      ...current,
                      financiers: [...current.financiers, { id: v4(), label: '' }],
                    })
                  }
                  type="button"
                  className="btn btn-link p-0 ms-2"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    className="bi bi-plus-square-fill"
                    viewBox="0 0 16 16"
                  >
                    <path d="M2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2zm6.5 4.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3a.5.5 0 0 1 1 0z" />
                  </svg>
                </button>
              )}
            </div>
            {current.financiers &&
              current.financiers.map((financier, index) => {
                const financierWarnings = warnings?.financiers && warnings.financiers[financierKey(financier, index)];
                return (
                  <div className="mb-2">
                    <div
                      className="d-flex"
                      key={`consumer-financier-${financier.id}-${index}`}
                    >
                      <div className="flex-fill">
                        <Financiers
                          any={false}
                          inputID="financierValue"
                          clearOnSelect={false}
                          value={financier}
                          warning={!!financierWarnings?.financier}
                          onChange={(payload) => {
                            current.financiers[index] = {
                              ...financier,
                              ...payload,
                            };
                            onChange({
                              target: {
                                name: "financiers",
                                value: [...current.financiers],
                              },
                            });
                          }}
                        />
                      </div>
                      <div className="flex-md-fill ms-2 position-relative">
                        <input
                          disabled={disabled}
                          autoCapitalize="words"
                          type="text"
                          name="identification"
                          placeholder={t("identification_placeholder", {
                            context: "consumers_financiers",
                          })}
                          value={financier.identification || ""}
                          onChange={onChangeFinancier(index)}
                          className={`form-control ${(financierWarnings?.identification || financierWarnings?.identification_format)
                              ? 'border-danger bg-danger-light focus-ring-danger'
                              : ''
                            }`}
                        />
                        {!disabled && (
                          <>
                            <div className="form-control-actions d-flex align-items-center gap-1 justify-content-between">
                              {financier.source && financier.source !== "drapp" && (
                                <div>
                                  <IconInfoCircle
                                    width="14"
                                    height="14"
                                    data-tooltip-id="app-tooltip"
                                    data-tooltip-html={`
                <div>
                  ${t("entered_by_patient", { context: "consumers", defaultValue: "Ingresado por paciente" })}
                </div>`}
                                  />
                                </div>
                              )}
                              <button
                                className="btn btn-sm text-danger py-0 px-1 ms-auto"
                                type="button"
                                onClick={() => {
                                  setCurrent({
                                    ...current,
                                    financiers: current.financiers.filter(
                                      (_, _index) => _index !== index
                                    ),
                                  });
                                }}
                              >
                                <svg
                                  width="1em"
                                  height="1em"
                                  viewBox="0 0 16 16"
                                  className="bi bi-x"
                                  fill="currentColor"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M11.854 4.146a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708-.708l7-7a.5.5 0 0 1 .708 0z"
                                  />
                                  <path
                                    fillRule="evenodd"
                                    d="M4.146 4.146a.5.5 0 0 0 0 .708l7 7a.5.5 0 0 0 .708-.708l-7-7a.5.5 0 0 0-.708 0z"
                                  />
                                </svg>
                              </button>
                            </div>
                          </>)
                        }
                      </div>
                    </div>
                    {financierWarnings?.identification_format && (
                      <div className="form-text text-danger">{t("financier_format_warning", "El número de afiliado de OSDE debe ser de 11 dígitos")}</div>
                    )}
                  </div>
                )
              })}
          </div>

          <Emails
            value={current.emails || []}
            disabled={disabled}
            onChange={(emails) =>
              onChange({ target: { name: "emails", value: emails } })
            }
            context="consumers"
            options={{ event: "all" }}
          />

          <Phones
            value={current.phones || []}
            disabled={disabled}
            onChange={(phones) =>
              onChange({ target: { name: "phones", value: phones } })
            }
            context="consumers"
          />

          <Input
            label={t("occupation", 'Ocupación', { context: "consumers" })}
            disabled={disabled}
            type="text"
            name="occupation"
            id="occupation"
            placeholder={t("occupation_placeholder", {
              context: "consumers",
            })}
            value={current.occupation || ""}
            onChange={onChange}
            hint={t("occupation_hint", 'Ocupación', { context: "consumers" })}
          />

          <Input
            label={t("address", 'Dirección', { context: "consumers" })}
            disabled={disabled}
            type="text"
            name="address"
            id="address"
            placeholder={t("address_placeholder", {
              context: "consumers",
            })}
            value={current.address || ""}
            onChange={onChange}
            hint={t("address_hint", 'Dirección', { context: "consumers" })}
          />

          <Text
            label={t("notes", { context: "consumers" })}
            id="notes"
            name="notes"
            value={current.notes || ""}
            onChange={onChange}
            placeholder={t("notes_placeholder", { context: "consumers" })}
            rows={1}
            hint={t("notes_hint", { context: "consumers" })}
          />
        </div>


        {showConfirm && (
          <div
            className="modal show d-flex align-items-center justify-content-center"
            tabIndex="-1"
            role="dialog"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", minHeight: "100vh" }}
          >
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{`${actionLabel} paciente`}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowConfirm(false)}
                  />
                </div>
                <div className="modal-body">
                  <p>{confirmMessage}</p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowConfirm(false)}
                  >
                    {t("cancel", "Cancelar")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      confirmAction();
                      setShowConfirm(false);
                    }}
                  >
                    {actionLabel}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Bug real (auditoría 2026-08-18, responsive): el grupo de la
            derecha (Atrás/Historia Clínica/Facturar sesiones/Cancelar/
            Guardar) es un d-flex sin flex-wrap — con los 3 links opcionales
            visibles (staff con resource propio + arca habilitado) suma
            ~450px, más ancho que el viewport a 375px. justify-content-
            between lo empuja pegado al borde derecho, así que Cancelar y
            Guardar quedaban totalmente fuera de pantalla y sin forma de
            tocarlos (el overflow-x:hidden global de src/index.scss los
            recorta en vez de dejarlos scrollear). */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 bg-white mt-4 pt-3 pl-2 border-top">
          {current && current.id ? (
        <div className="d-flex align-items-center">
            <div className="dropdown">
              <button
                className="btn btn-link p-0 m-2"
                type="button"
                id="dropdownMenuButton"
                data-bs-toggle="dropdown"
                data-tooltip-id="app-tooltip"
                data-tooltip-content={t("more_options")}
                aria-expanded="false"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-three-dots-vertical text-muted" viewBox="0 0 16 16">
                  <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0"/>
                </svg>
              </button>
              <ul
                className="dropdown-menu dropdown-menu-start shadow-sm"
                aria-labelledby="dropdownMenuButton"
              >
                <li>
                  <button
                    className="dropdown-item text-muted d-flex align-items-center"
                    onClick={() =>
                      openConfirm(
                        () => handleBlockClick(),
                        isBlocked
                          ? t(
                            "consumer_unblock_message_hint",
                            "Al desbloquear el paciente, se le permitirá gestionar sus turnos online."
                          )
                          : t(
                            "consumer_block_message_hint",
                            "Al bloquear el paciente, no podrá gestionar sus turnos online."
                          ),
                        isBlocked
                          ? t("unblock_patient", "Desbloquear")
                          : t("block_patient", "Bloquear")
                      )
                    }
                  >
                    <i className="fas fa-ban me-2"></i>
                    {isBlocked
                      ? t("unblock_patient_button", "Desbloquear Paciente")
                      : t("block_patient_button", "Bloquear Paciente")}
                  </button>
                </li>
                {props.remove !== false && current.id && !disabled && (
                  <li>
                    <button
                      className="dropdown-item text-danger d-flex align-items-center"
                      onClick={() =>
                        openConfirm(
                          () => toggle(),
                          current.deleted
                            ? t(
                              "restore_message",
                              "Estás seguro que deseas restaurar este paciente?"
                            )
                            : t(
                              "delete_message",
                              "Estás seguro que deseas eliminar este paciente? Este proceso se puede deshacer"
                            ),
                          current.deleted
                            ? t("restore_patient", "Restaurar")
                            : t("delete_patient", "Eliminar")
                        )
                      }
                    >
                      <i className="fas fa-trash me-2"></i>
                      {current.deleted
                        ? t("restore_patient", "Restaurar Paciente")
                        : t("delete_patient", "Eliminar Paciente")}
                    </button>
                  </li>
                )}
              </ul>
            </div>
            {
              isBlocked && 
              <button
              className="btn btn-outline-secondary text-secondary me-3"
              onClick={() =>
                openConfirm(
                  () => handleBlockClick(),
                  t(
                      "consumer_unblock_message_hint",
                      "Al desbloquear el paciente, se le permitirá gestionar sus turnos online."
                    ),
                  t("unblock_patient", "Desbloquear")
                )
              }
            >
              <i className="fas fa-ban me-2"></i>
              {("unblock_patient_button", "Desbloquear Paciente")}
            </button>
          }
        </div>
          ) : (
            <div />
            )}
          <div className="d-flex align-items-center flex-wrap gap-2">
          {props.onClose ? (
                  <button
                    type="button"
                    onClick={() => props.onClose({ ...current })}
                    className="btn btn-link link-dark"
                    data-bs-dismiss={props.modal === false ? '' : 'modal'}
                  >
                    {t("back")}
                  </button>
                ) : props.callback ? (
                  <button
                    type="button"
                    onClick={() => props.callback({ ...current })}
                    className="btn btn-link link-dark"
                    data-bs-dismiss={props.modal === false ? '' : 'modal'}
                  >
                    {t("back")}
                  </button>
                ) : props.back !== false && (
                  <Link className="btn btn-link link-secondary" to="../">
                    {t("back")}
                  </Link>
                )}
            {
              current.id && (user.state.isStaff ||
              (user.actions.has("ehr") && user.team?.resourceID)) && (
                    <Link
                      className="btn btn-link"
                      to={window._l(`/${current.id}/ehr`)}
                    >
                      {t("ehr", { context: "patients" })}
                    </Link>
                  )}
            {/* Facturación en lote (§negocio 2026-08-04) — mismo gate que EHR
                (profesional con resource propio) MÁS arcaEnabled (bug real,
                ver comentario junto a arcaEnabledQuery más arriba): sin este
                último chequeo, el link aparecía en TODO team sin el feature
                flag y llevaba a una página en blanco. */}
            {current.id && user.team?.resourceID && arcaEnabled && (
              <Link className="btn btn-link" to={window._l(`/${current.id}/facturar`)}>
                {t("arca_batch_link", "Facturar sesiones")}
              </Link>
            )}
            {current.id && <SendFilesButton events={events.data} onSent={() => setSharesRefreshKey((k) => k + 1)} />}
            <button
              type="button"
              className="btn btn-outline-secondary text-secondary me-3"
              disabled={!changed}
              onClick={() => setCurrent(clone(original))}
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!changed}
            >
              {t("save")}
            </button>
          </div>
        </div>
      </Form>

      {showModal && (
        <div className="modal show d-flex align-items-center justify-content-center" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", minHeight: "100vh" }}>
          {/* Fix responsive (2026-08-14): 30vw da ~110px en un celular
              (viewport angosto) — ni el título entraba. min() da un ancho
              de modal razonable en cualquier tamaño. */}
          <div className="modal-dialog" role="document" style={{ width: "min(400px, calc(100vw - 32px))" }}>
            <div className="modal-content">
              <div className="modal-header">
              <h5 className="modal-title">{t("success", "Paciente creado")}</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <p>{t("consumer_created", "El paciente ha sido creado exitosamente.")}</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-primary" onClick={() => setShowModal(false)}>
                  {t("ok", "Aceptar")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {events.data?.next?.map((event) => (
        <Event key={event.id} event={event} consumer={original} />
      ))}

      {pastEvents.slice(0, visiblePastCount).map((event) => (
        <Event key={event.id} event={event} consumer={original} />
      ))}

      {hasMorePast && <div ref={pastEndRef} className="py-2" aria-hidden />}

      {current.id && <ConsumerSharesHistory consumerID={current.id} refreshKey={sharesRefreshKey} />}
    </>
  );
};

export default App;
